import React, { useState, useEffect } from 'react';
import { CogIcon, CalculatorIcon } from '@heroicons/react/24/outline';

const SettingsSection = ({ api }) => {
  const [tokensPerRuble, setTokensPerRuble] = useState('1000');
  const [costPer1000, setCostPer1000] = useState('0.5');
  const [defaultTokens, setDefaultTokens] = useState('1000');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTokensPerRuble(data.tokens_per_ruble?.value || '1000');
        setCostPer1000(data.cost_per_1000_tokens?.value || '0.5');
        setDefaultTokens(data.default_tokens_on_registration?.value || '1000');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveSetting = async (key, value) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`/api/admin/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: String(value) })
      });
      if (res.ok) {
        setMessage('Сохранено!');
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage('Ошибка сохранения');
      }
    } catch (e) {
      setMessage('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Расчёты калькулятора
  const tpr = parseFloat(tokensPerRuble) || 1000;
  const cost = parseFloat(costPer1000) || 0.5;
  
  const sellPrice1000 = 1000 / tpr; // Цена продажи 1000 токенов
  const margin = sellPrice1000 - cost; // Маржа с 1000 токенов
  const marginPercent = cost > 0 ? ((margin / cost) * 100) : 0;
  
  // Примеры пакетов
  const packages = [100, 500, 1000, 5000];

  if (loading) return <div className="text-center py-8 text-gray-500">Загрузка...</div>;

  return (
    <div className="space-y-6">
      {/* Основные настройки */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <CogIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Настройки ценообразования</h2>
        </div>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Стартовые токены при регистрации
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={defaultTokens}
                onChange={(e) => setDefaultTokens(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                max="2147483647"
              />
              <button
                onClick={() => saveSetting('default_tokens_on_registration', defaultTokens)}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
              >
                Сохранить
              </button>
            </div>
            <p className="text-xs text-gray-500">Количество токенов, которые получает новый пользователь</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Токенов за 1 ₽ (цена продажи)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={tokensPerRuble}
                  onChange={(e) => setTokensPerRuble(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  min="1"
                />
                <button
                  onClick={() => saveSetting('tokens_per_ruble', tokensPerRuble)}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                >
                  Сохранить
                </button>
              </div>
              <p className="text-xs text-gray-500">Чем больше значение, тем дешевле токены для пользователя</p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Закупочная цена за 1000 токенов (₽)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={costPer1000}
                  onChange={(e) => setCostPer1000(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  min="0"
                />
                <button
                  onClick={() => saveSetting('cost_per_1000_tokens', costPer1000)}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                >
                  Сохранить
                </button>
              </div>
              <p className="text-xs text-gray-500">Ваши затраты на API (GigaChat и т.д.)</p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mt-3 text-sm ${message === 'Сохранено!' ? 'text-green-600' : 'text-red-600'}`}>
            {message}
          </div>
        )}
      </section>

      {/* Калькулятор маржи */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalculatorIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Калькулятор маржи</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{sellPrice1000.toFixed(2)} ₽</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Цена продажи 1000 токенов</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-red-500">{cost.toFixed(2)} ₽</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Себестоимость 1000 токенов</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center shadow-sm">
            <div className={`text-2xl font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {margin >= 0 ? '+' : ''}{margin.toFixed(2)} ₽
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Маржа с 1000 токенов</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center shadow-sm">
            <div className={`text-2xl font-bold ${marginPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {marginPercent >= 0 ? '+' : ''}{marginPercent.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Наценка</div>
          </div>
        </div>

        {/* Таблица пакетов */}
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Расчёт по пакетам</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-3 py-2 text-left rounded-tl-lg">Сумма покупки</th>
                <th className="px-3 py-2 text-right">Токенов</th>
                <th className="px-3 py-2 text-right">Себестоимость</th>
                <th className="px-3 py-2 text-right">Прибыль</th>
                <th className="px-3 py-2 text-right rounded-tr-lg">Маржа %</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((amount, idx) => {
                const tokens = amount * tpr;
                const costTotal = (tokens / 1000) * cost;
                const profit = amount - costTotal;
                const profitPercent = costTotal > 0 ? ((profit / costTotal) * 100) : 0;
                return (
                  <tr key={amount} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                    <td className="px-3 py-2 font-medium">{amount.toLocaleString()} ₽</td>
                    <td className="px-3 py-2 text-right text-blue-600 font-semibold">{tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-red-500">{costTotal.toFixed(2)} ₽</td>
                    <td className={`px-3 py-2 text-right font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profit >= 0 ? '+' : ''}{profit.toFixed(2)} ₽
                    </td>
                    <td className={`px-3 py-2 text-right ${profitPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profitPercent >= 0 ? '+' : ''}{profitPercent.toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            💡 <strong>Рекомендация:</strong> Для устойчивой бизнес-модели рекомендуется маржа не менее 50%. 
            Текущая маржа: <strong className={marginPercent >= 50 ? 'text-green-600' : 'text-red-600'}>{marginPercent.toFixed(0)}%</strong>
          </p>
        </div>
      </section>
    </div>
  );
};

export default SettingsSection;
